import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trophy, Medal, Crown, Star } from '@phosphor-icons/react';
import { API } from '@/App';
import { useLanguage } from '@/i18n/LanguageContext';

const t = {
  en: { title: "Winners Hall of Fame", subtitle: "Previous draw champions", draw: "Draw", grandPrize: "Grand Prize", secondPrize: "2nd Prize Winners", noDraws: "No completed draws yet. Be part of the first one!", tickets: "tickets sold" },
  sk: { title: "Sie\u0148 sl\u00E1vy v\u00FD\u0165azov", subtitle: "V\u00ED\u0165azi predch\u00E1dzaj\u00FAcich k\u00F4l", draw: "Kolo", grandPrize: "Hlavn\u00E1 v\u00FDhra", secondPrize: "V\u00FD\u0165azi 2. ceny", noDraws: "Zatia\u013E \u017Eiadne dokon\u010Den\u00E9 kol\u00E1. Bu\u010Fte s\u00FA\u010Das\u0165ou prv\u00E9ho!", tickets: "predan\u00FDch l\u00EDstkov" },
  de: { title: "Gewinner-Ruhmeshalle", subtitle: "Champions fr\u00FCherer Ziehungen", draw: "Ziehung", grandPrize: "Hauptgewinn", secondPrize: "2. Preis Gewinner", noDraws: "Noch keine abgeschlossenen Ziehungen. Seien Sie beim ersten dabei!", tickets: "verkaufte Tickets" }
};

const HallOfFame = () => {
  const [draws, setDraws] = useState([]);
  const [loading, setLoading] = useState(true);
  const { language } = useLanguage();
  const copy = t[language] || t.en;

  useEffect(() => {
    fetch(`${API}/draws/hall-of-fame`)
      .then(r => r.json())
      .then(data => { setDraws(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return null;

  return (
    <section className="py-20 px-6 relative" data-testid="hall-of-fame">
      <div className="max-w-5xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }} transition={{ duration: 0.6 }} className="text-center mb-12">
          <div className="inline-flex items-center gap-2 mb-4">
            <Trophy size={28} weight="duotone" className="text-[#cba135]" />
            <h2 className="heading-font text-3xl sm:text-4xl font-bold text-[#cba135]">{copy.title}</h2>
            <Trophy size={28} weight="duotone" className="text-[#cba135]" />
          </div>
          <p className="text-[#cba135]/60 text-sm">{copy.subtitle}</p>
        </motion.div>

        {draws.length === 0 ? (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            className="text-center py-12 border border-[#cba135]/10 rounded-2xl bg-[#1a1508]/30">
            <Star size={40} weight="duotone" className="text-[#cba135]/30 mx-auto mb-4" />
            <p className="text-[#cba135]/50">{copy.noDraws}</p>
          </motion.div>
        ) : (
          <div className="space-y-6">
            <AnimatePresence>
              {draws.map((draw, i) => (
                <motion.div key={draw.drawNumber}
                  initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1, duration: 0.5 }}
                  className="bg-gradient-to-br from-[#1a1508]/80 to-[#0e0b04]/80 border border-[#cba135]/15 rounded-2xl overflow-hidden">
                  
                  <div className="p-6 border-b border-[#cba135]/10 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-[#cba135]/10 flex items-center justify-center">
                        <Crown size={22} weight="duotone" className="text-[#cba135]" />
                      </div>
                      <div>
                        <h3 className="heading-font text-xl text-[#cba135] font-bold">{copy.draw} #{draw.drawNumber}</h3>
                        <p className="text-xs text-[#cba135]/40">{draw.ticketsSold} {copy.tickets}</p>
                      </div>
                    </div>
                    {draw.completedAt && (
                      <span className="text-xs text-[#cba135]/40">{new Date(draw.completedAt).toLocaleDateString(language === 'sk' ? 'sk-SK' : language === 'de' ? 'de-DE' : 'en-US')}</span>
                    )}
                  </div>

                  <div className="p-6">
                    <div className="mb-6 p-4 rounded-xl bg-[#cba135]/5 border border-[#cba135]/10">
                      <div className="flex items-center gap-2 mb-2">
                        <Trophy size={20} weight="fill" className="text-[#cba135]" />
                        <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#cba135]/70">{copy.grandPrize}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-[#cba135] font-bold text-lg">{draw.firstPrize.username}</span>
                        <span className="heading-font text-2xl font-bold text-[#cba135]">EUR {draw.firstPrize.amount.toLocaleString()}</span>
                      </div>
                    </div>

                    {draw.secondPrize.length > 0 && (
                      <div>
                        <div className="flex items-center gap-2 mb-3">
                          <Medal size={18} weight="duotone" className="text-[#cba135]/70" />
                          <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#cba135]/50">{copy.secondPrize} ({draw.secondPrize.length}x)</span>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                          {draw.secondPrize.map((w) => (
                            <div key={`sp-${draw.drawNumber}-${w.username}-${w.ticketNumber || w.amount}`} className="text-center py-2 px-3 rounded-lg bg-[#0e0b04] border border-[#cba135]/5">
                              <p className="text-sm text-[#cba135]/80 truncate">{w.username}</p>
                              <p className="text-xs text-[#cba135]/50">EUR {w.amount.toLocaleString()}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>
    </section>
  );
};

export default HallOfFame;
